import type { LayerDiagnostic } from "@/v2-backoffice-client/layer/update/diagnostics/layer.update.diagnostics.utils"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"
import type { LayerDriftEntry } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import {
  referencesToken,
  REMEDIATION_RENAME_TOKEN,
} from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"

/**
 * One upstream change that the document in the editor still refers to.
 * Carries the token so the caller can locate it in the text, rather than
 * a pre-rendered position — the operator is editing, and any position we
 * computed would be stale by the next keystroke.
 */
export type LayerDriftFinding = {
  token: string
  renameTo: string | null
  severity: "error" | "warning"
  message: string
}

/**
 * The drift entries that concern one document, as findings.
 *
 * Only entries carrying a remediation qualify: the backend attaches one
 * exactly when a layer document names the thing that changed, which is
 * also the only case where there is something in the editor to point at.
 * An upstream change the layer never mentions belongs on the Drift tab,
 * not in the middle of someone's Configuration.
 */
export function getDriftFindings(
  entries: readonly LayerDriftEntry[],
  field: LayerEditorField,
): LayerDriftFinding[] {
  const findings: LayerDriftFinding[] = []

  for (const entry of entries) {
    const remediation = entry.Remediation
    if (!remediation?.FromToken) continue
    if (!remediation.Documents.includes(field)) continue

    const token = remediation.FromToken
    const renameTo =
      remediation.Kind === REMEDIATION_RENAME_TOKEN
        ? (remediation.ToToken ?? null)
        : null

    findings.push({
      token,
      renameTo,
      severity: entry.Severity === "breaking" ? "error" : "warning",
      message: renameTo
        ? `Upstream renamed "${token}" to "${renameTo}" — this document still references the old name.`
        : `"${token}" no longer exists upstream — this document still references it.`,
    })
  }

  return findings
}

/**
 * Narrows findings to the ones the current draft still contains, so a
 * fix stops being flagged as it is typed instead of surviving until the
 * next save and probe.
 */
export function getLiveDriftFindings(
  findings: readonly LayerDriftFinding[],
  draft: string,
): LayerDriftFinding[] {
  return findings.filter((finding) => referencesToken(draft, finding.token))
}

/** Findings in the shape the diagnostics pill already renders. */
export function toDriftDiagnostics(
  findings: readonly LayerDriftFinding[],
): LayerDiagnostic[] {
  return findings.map((finding) => ({
    severity: finding.severity,
    message: finding.message,
  }))
}
